import React from "react";
import {
    Card,
    CardBody,
    Container,
    Row,
    Col,
    Pagination,
    PaginationItem,
    PaginationLink,
    Input,
    Button,
    InputGroup,
    Form
} from "reactstrap";
import { useNavigate, Navigate } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

const PAGE_SIZE = 25;

function getSurname(name) {
    const parts = name.trim().split(/\s+/);
    return parts[parts.length - 1];
}

function toSortKey(name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return name.toLowerCase();
    const surname = parts[parts.length - 1];
    const firstNames = parts.slice(0, -1).join(' ');
    return `${surname} ${firstNames}`.toLowerCase();
}

function toDisplayName(name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return name;
    const surname = parts[parts.length - 1];
    const firstNames = parts.slice(0, -1).join(' ');
    return `${surname}, ${firstNames}`;
}

class AdminAuthors extends React.Component {
    state = {
        isLoading: false,
        allAuthors: [],
        filteredAuthors: [],
        currentPage: 0,
        searchQuery: "",
        unauthorized: false
    };

    componentDidMount() {
        try {
            const customer = sessionStorage.getItem("customer");
            const parsed = customer ? JSON.parse(customer) : null;
            if (parsed?.user?.isAdmin) {
                this.loadAuthors();
            } else {
                this.setState({ unauthorized: true });
            }
        } catch (e) {
            console.error("AdminAuthors componentDidMount error:", e);
            this.setState({ unauthorized: true });
        }
    }

    loadAuthors = () => {
        this.setState({ isLoading: true });
        Api.get("/authors")
            .then(({ data }) => {
                const sorted = (data || []).sort((a, b) =>
                    toSortKey(a.name).localeCompare(toSortKey(b.name))
                );
                this.setState({
                    allAuthors: sorted,
                    filteredAuthors: sorted,
                    isLoading: false
                });
            })
            .catch(err => {
                console.error("Error fetching authors:", err);
                this.setState({ isLoading: false });
            });
    };

    handleSearchChange = (e) => this.setState({ searchQuery: e.target.value });

    handleSearchSubmit = (e) => {
        e.preventDefault();
        const q = this.state.searchQuery.trim().toLowerCase();
        const filtered = q
            ? this.state.allAuthors.filter(a => a.name.toLowerCase().includes(q))
            : this.state.allAuthors;
        this.setState({ filteredAuthors: filtered, currentPage: 0 });
    };

    handleClearSearch = () => {
        this.setState({
            searchQuery: "",
            filteredAuthors: this.state.allAuthors,
            currentPage: 0
        });
    };

    renderPagination() {
        const { currentPage, filteredAuthors } = this.state;
        const totalPages = Math.ceil(filteredAuthors.length / PAGE_SIZE);
        if (totalPages <= 1) return null;

        const max = 5;
        let start = Math.max(0, currentPage - Math.floor(max / 2));
        let end = Math.min(totalPages - 1, start + max - 1);
        if (end - start < max - 1) start = Math.max(0, end - (max - 1));

        const pages = [];
        for (let i = start; i <= end; i++) {
            pages.push(
                <PaginationItem active={i === currentPage} key={i}>
                    <PaginationLink onClick={() => this.setState({ currentPage: i })}>
                        {i + 1}
                    </PaginationLink>
                </PaginationItem>
            );
        }

        return (
            <Pagination className="justify-content-center mt-4 flex-wrap">
                <PaginationItem disabled={currentPage === 0}>
                    <PaginationLink previous onClick={() => this.setState({ currentPage: currentPage - 1 })} />
                </PaginationItem>
                {start > 0 && (
                    <>
                        <PaginationItem>
                            <PaginationLink onClick={() => this.setState({ currentPage: 0 })}>1</PaginationLink>
                        </PaginationItem>
                        <PaginationItem disabled><PaginationLink>…</PaginationLink></PaginationItem>
                    </>
                )}
                {pages}
                {end < totalPages - 1 && (
                    <>
                        <PaginationItem disabled><PaginationLink>…</PaginationLink></PaginationItem>
                        <PaginationItem>
                            <PaginationLink onClick={() => this.setState({ currentPage: totalPages - 1 })}>
                                {totalPages}
                            </PaginationLink>
                        </PaginationItem>
                    </>
                )}
                <PaginationItem disabled={currentPage === totalPages - 1}>
                    <PaginationLink next onClick={() => this.setState({ currentPage: currentPage + 1 })} />
                </PaginationItem>
            </Pagination>
        );
    }

    render() {
        if (!sessionStorage.getItem("customer")) return sessionEnded();
        if (this.state.unauthorized) return <Navigate to="/" />;

        const { filteredAuthors, isLoading, searchQuery, currentPage } = this.state;
        const totalPages = Math.ceil(filteredAuthors.length / PAGE_SIZE);
        const pageAuthors = filteredAuthors.slice(
            currentPage * PAGE_SIZE,
            (currentPage + 1) * PAGE_SIZE
        );

        return (
            <>
                <SeoNoIndex noindex={true} />

                <div className="position-relative">
                    <section className="section section-lg section-shaped pb-250">
                        <div className="shape shape-style-1 shape-default">
                            <span /><span /><span /><span /><span /><span /><span /><span /><span />
                        </div>
                        <Container className="py-lg-md d-flex">
                            <div className="col px-0">
                                <Row>
                                    <Col lg="8">
                                        <h1 className="display-3 text-white">
                                            Building your song-flows<br /><span>manage authors</span>
                                        </h1>
                                    </Col>
                                </Row>
                            </div>
                        </Container>
                        <div className="separator separator-bottom separator-skew">
                            <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                                <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                            </svg>
                        </div>
                    </section>

                    <section className="section section-lg pt-lg-0 mt--200">
                        <Container>
                            <Row className="justify-content-center">
                                <Col lg="12">
                                    <Row className="row-grid">
                                        <Col lg="12">
                                            <Card className="shadow border-0">
                                                <Row className="row-grid">
                                                    <CardBody className="py-5">
                                                        <Button
                                                            color="link"
                                                            onClick={() => this.props.navigate("/admin")}
                                                            className="mb-3 p-0"
                                                        >
                                                            &larr; Back to menu
                                                        </Button>

                                                        <Form inline onSubmit={this.handleSearchSubmit} className="mb-3">
                                                            <InputGroup>
                                                                <Input
                                                                    type="text"
                                                                    placeholder="Search authors..."
                                                                    value={searchQuery}
                                                                    onChange={this.handleSearchChange}
                                                                />
                                                                <Button color="primary" type="submit">Search</Button>
                                                                <Button
                                                                    color="secondary"
                                                                    onClick={this.handleClearSearch}
                                                                    disabled={!searchQuery.trim()}
                                                                    className="ml-2"
                                                                >
                                                                    Clear
                                                                </Button>
                                                            </InputGroup>
                                                        </Form>

                                                        <div className="text-muted small mb-2">
                                                            {filteredAuthors.length} author{filteredAuthors.length !== 1 ? 's' : ''}
                                                            {totalPages > 1 && ` — page ${currentPage + 1} of ${totalPages}`}
                                                        </div>

                                                        {isLoading ? (
                                                            <div>Loading…</div>
                                                        ) : (
                                                            <ul className="list-group">
                                                                {pageAuthors.length > 0 ? pageAuthors.map(a => (
                                                                    <li
                                                                        key={a.id}
                                                                        className="list-group-item"
                                                                        role="button"
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => this.props.navigate(`/admin/author/${a.id}`)}
                                                                    >
                                                                        <strong>{toDisplayName(a.name)}</strong>
                                                                        <div className="small text-muted">({a.id})</div>
                                                                    </li>
                                                                )) : (
                                                                    <li className="list-group-item">No authors found.</li>
                                                                )}
                                                            </ul>
                                                        )}

                                                        {this.renderPagination()}
                                                    </CardBody>
                                                </Row>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Container>
                    </section>
                </div>
            </>
        );
    }
}

export default withNavigateHook(AdminAuthors);

function withNavigateHook(Component) {
    return props => {
        const navigate = useNavigate();
        return <Component {...props} navigate={navigate} />;
    };
}
